import React from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import adminApi from "services/admin-api";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { AdminUserForm } from "components/admin/forms";
import { PageHeader, SectionCard } from "components/admin/ui";
import { isRecoverableUploadResponseError } from "utils/admin";

export const ProfileEditPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["profile"],
    queryFn: adminApi.currentUser,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: any) =>
      adminApi.updateCurrentUser({
        ...payload,
        role: undefined,
        password: payload.password || undefined,
        password_confirmation: payload.password_confirmation || undefined,
      }),
    onSuccess: () => {
      toast.success("Profile updated.");
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["showUserDetails"] });
      history.push("/profile");
    },
    onError: (error: any, variables: any) => {
      if (isRecoverableUploadResponseError(error, variables)) {
        toast.success("Profile updated and image refreshed.");
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["showUserDetails"] });
        history.push("/profile");
        return;
      }

      toast.error(error?.response?.data?.error || "Unable to update your profile.");
    },
  });

  return (
    <AppLayout route="/profile">
      <PageHeader
        title="Edit Profile"
        description="Update your account details without leaving your current Athspire workspace."
        crumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Profile", to: "/profile" },
          { label: "Edit profile" },
        ]}
      />

      <SectionCard subtitle="Changes save directly to your user record" title="Profile Editor">
        <AdminUserForm
          includeRole={false}
          initialValues={{
            first_name: user?.first_name || "",
            last_name: user?.last_name || "",
            email: user?.email || "",
            phone_number: user?.phone_number || "",
            street_address: user?.street_address || "",
            city: user?.city || "",
            state: user?.state || "",
            country: user?.country || "",
            zip_code: user?.zip_code || "",
            image: user?.image || "",
            password: "",
            password_confirmation: "",
          }}
          isSubmitting={updateMutation.isPending}
          onCancel={() => history.push("/profile")}
          onSubmit={(values) => updateMutation.mutate(values)}
          requirePassword={false}
          submitLabel="Save profile"
        />
      </SectionCard>
    </AppLayout>
  );
};

export default ProfileEditPage;
